import React, { useState, useCallback } from "react";
import {
  DropZone,
  Card,
  Banner,
  CalloutCard,
  Text,
  Spinner,
} from "@shopify/polaris";
import { ExportIcon } from "@shopify/polaris-icons";
import Papa from "papaparse";

/**
 * Reusable CSV import + export panel.
 * Append-only — never deletes existing data.
 *
 * Props:
 *  - title: section title (e.g. "Fabric Numbers")
 *  - csvField: required CSV column name (e.g. "fabricNumber")
 *  - apiBase: base endpoint, e.g. "/api/fabrics" or "/api/linings"
 *  - payloadKey: body wrapper key for import, e.g. "fabrics" or "linings"
 *  - exportFilename: prefix for downloaded csv (e.g. "fabric-export")
 *  - onImportSuccess: optional callback after successful import
 */
export default function CsvImportExport({
  title,
  csvField,
  apiBase,
  payloadKey,
  exportFilename,
  onImportSuccess,
}) {
  const baseUrl = process.env.REACT_APP_API_URL;
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const getToken = () => localStorage.getItem("token");

  const handleExport = async () => {
    const token = getToken();
    if (!token) {
      setError("User not authenticated.");
      return;
    }
    setError("");
    setExporting(true);
    try {
      const response = await fetch(`${baseUrl}${apiBase}/export`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, Accept: "text/csv" },
      });

      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const filename = `${exportFilename}-${new Date().toISOString().split("T")[0]}.csv`;

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleDropZoneDrop = useCallback(
    (_dropFiles, acceptedFiles) => {
      setError("");
      setSuccess("");

      const file = acceptedFiles[0];
      if (!file || file.type !== "text/csv") {
        setError("Only .csv files are allowed.");
        return;
      }

      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        setError("File size should not exceed 2MB.");
        return;
      }

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => (h || "").trim(),
        complete: (result) => {
          const data = result.data;
          if (!Array.isArray(data) || data.length === 0) {
            setError("CSV file is empty or invalid.");
            return;
          }
          const firstRow = data[0];
          if (!(csvField in firstRow)) {
            setError(`Missing required column: "${csvField}".`);
            return;
          }
          sendToAPI(data);
        },
        error: () => setError("Failed to parse the CSV file."),
      });
    },
    [csvField]
  );

  const sendToAPI = async (data) => {
    const token = getToken();
    if (!token) return;

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(`${baseUrl}${apiBase}/import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ [payloadKey]: data }),
      });

      const result = await response.json();
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Import failed.");
      }

      const sum = result.summary || {};
      const parts = [];
      if (typeof sum.added === "number") parts.push(`Added ${sum.added}`);
      if (sum.skippedExisting) parts.push(`Skipped ${sum.skippedExisting} existing`);
      if (sum.duplicatesInCSV) parts.push(`Ignored ${sum.duplicatesInCSV} duplicate row(s)`);
      setSuccess(parts.length ? parts.join(" · ") : result.message);

      if (onImportSuccess) onImportSuccess();
    } catch (err) {
      setError(err.message || "Failed to upload data.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="file_csv">
      <CalloutCard
        title={`Export ${title}`}
        primaryAction={{
          content: `Export ${title}`,
          onAction: handleExport,
          icon: ExportIcon,
          variant: "primary",
          loading: exporting,
        }}
      >
        <p>
          1. Export the {title} CSV file. <br />
          2. Edit the file as needed using any spreadsheet editor. <br />
          3. Save the file in CSV format. <br />
          4. Import the updated CSV back into the system. <br />
          <strong>Required column:</strong> <code>{csvField}</code>
        </p>
      </CalloutCard>

      <div className="fabric_csv">
        <Card sectioned>
          {error && <Banner status="critical">{error}</Banner>}
          {success && <Banner status="success">{success}</Banner>}

          {!isLoading && (
            <>
              <span className="csv_title">Import CSV File</span>
              <DropZone accept=".csv" type="file" onDrop={handleDropZoneDrop}>
                <DropZone.FileUpload />
              </DropZone>
              <p style={{ marginTop: 8, color: "#637381", fontSize: 13 }}>
                Existing entries are preserved. Duplicates are skipped automatically.
              </p>
            </>
          )}

          {isLoading && (
            <>
              <Spinner accessibilityLabel="Uploading CSV" size="small" />
              <Text>Uploading CSV data, please wait...</Text>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
